import { create } from 'zustand';

export interface Toast {
  id: string;
  variant: 'success' | 'error' | 'warning' | 'info';
  title: string;
  description?: string;
  duration?: number;
  action?: { label: string; onClick: () => void };
}

interface UIState {
  // Sidebar
  sidebarCollapsed: boolean;
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;

  // Command palette
  commandPaletteOpen: boolean;
  openCommandPalette: () => void;
  closeCommandPalette: () => void;

  // Theme
  theme: 'dark' | 'light';
  toggleTheme: () => void;

  // AI Chat
  chatOpen: boolean;
  toggleChat: () => void;

  // Toasts
  toasts: Toast[];
  addToast: (toast: Omit<Toast, 'id'>) => void;
  removeToast: (id: string) => void;
}

export const useUIStore = create<UIState>((set) => ({
  // Sidebar
  sidebarCollapsed: false,
  toggleSidebar: () => set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),
  setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),

  // Command palette
  commandPaletteOpen: false,
  openCommandPalette: () => set({ commandPaletteOpen: true }),
  closeCommandPalette: () => set({ commandPaletteOpen: false }),

  // Theme
  theme: (typeof window !== 'undefined' && (localStorage.getItem('electroplm-theme') as 'dark' | 'light'))
    || (typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'),
  toggleTheme: () =>
    set((s) => {
      const next = s.theme === 'dark' ? 'light' : 'dark';
      localStorage.setItem('electroplm-theme', next);
      document.documentElement.classList.toggle('dark', next === 'dark');
      document.documentElement.classList.toggle('light', next === 'light');
      return { theme: next };
    }),

  // AI Chat
  chatOpen: false,
  toggleChat: () => set((s) => ({ chatOpen: !s.chatOpen })),

  // Toasts
  toasts: [],
  addToast: (toast) =>
    set((s) => ({
      toasts: [...s.toasts, { ...toast, id: crypto.randomUUID() }],
    })),
  removeToast: (id) =>
    set((s) => ({
      toasts: s.toasts.filter((t) => t.id !== id),
    })),
}));

/** Convenience helper for toast calls */
export const toast = {
  success: (title: string, description?: string) =>
    useUIStore.getState().addToast({ variant: 'success', title, description }),
  error: (title: string, description?: string) =>
    useUIStore.getState().addToast({ variant: 'error', title, description }),
  warning: (title: string, description?: string) =>
    useUIStore.getState().addToast({ variant: 'warning', title, description }),
  info: (title: string, description?: string) =>
    useUIStore.getState().addToast({ variant: 'info', title, description }),
  /** Show a toast with an undo action. Returns after the undo window expires. */
  withUndo: (title: string, opts: {
    description?: string;
    undoLabel?: string;
    onUndo: () => void;
    duration?: number;
  }) => {
    useUIStore.getState().addToast({
      variant: 'info',
      title,
      description: opts.description,
      duration: opts.duration || 5000,
      action: { label: opts.undoLabel || 'Undo', onClick: opts.onUndo },
    });
  },
};
